import { useEffect, useMemo } from 'react';
import { Form, FormInstance } from 'antd';
import styled from 'styled-components';
import {
  Button,
  Colors,
  IcCashierSave,
  // IcPause,
  IcPublish,
  IcShow,
  IcTrash,
  Row,
  SelectWithLabel,
} from 'basic-ui-lib';
import { LayoutCode } from 'modules/channel/services/constants';
import { LayoutSelector } from '../LayoutSelector';
import useGetListStores from 'modules/store/services/hooks/useGetListStore';
import { ScreenFormValues } from '../ScreenForm';
import toast from 'utils/toast';
import { getMessageError } from 'services/response';
import useGetListPlaylists from 'modules/playlist/services/hooks/useGetListPlaylists';
import playlistApis from 'modules/playlist/services/apis';
import { useDeleteScreen } from 'modules/dashboard/services/hooks/useDeleteScreen';
import { useNavigate } from 'react-router-dom';

interface EditorHeaderProps {
  form: FormInstance<ScreenFormValues>;
  screenId?: string;
  screenStatus?: string;
  isCreateMode?: boolean;
  onSaveDraft?: () => void;
  onDeactivate?: () => void;
  onPause?: () => void;
  onReactivate?: () => void;
  onUpdate?: () => void;
}

const EditorHeader = ({
  form,
  screenId,
  screenStatus,
  isCreateMode,
  onSaveDraft,
  // onPause,
  onUpdate,
}: EditorHeaderProps) => {
  const navigate = useNavigate();
  const { storesList: storesData, mutate: fetchStores } = useGetListStores(true);
  const [playlistsData, , fetchPlaylists] = useGetListPlaylists(true);

  const { onDelete, context: DeleteModal } = useDeleteScreen();

  useEffect(() => {
    fetchStores({ page: 1, size: 50 });
    fetchPlaylists({ page: 1, size: 50 });
  }, []);

  const storeOptions = useMemo(() => {
    const stores = storesData?.content ?? [];
    return stores.map((store) => ({
      label: store.storeName,
      value: store.storeId,
    }));
  }, [storesData]);

  const playlistOptions = useMemo(() => {
    const playlists = playlistsData?.content ?? [];
    return playlists.map((playlist) => ({
      label: playlist.name,
      value: playlist.id,
    }));
  }, [playlistsData]);

  const handleLayoutChange = (layoutCode: LayoutCode) => {
    form.setFieldsValue({
      layoutCode,
    });
  };

  const handleStoreChange = (storeId: string) => {
    form.setFieldsValue({ storeId });
  };

  const handlePlaylistChange = async (playListId: string) => {
    form.setFieldsValue({ playListId });

    if (!playListId) return;

    try {
      const res = await playlistApis.getDetailPlaylist(playListId);
      if (res?.data?.data) {
        const playlistDetail = res.data.data;

        form.setFieldsValue({
          contentItems: playlistDetail.contentItems,
        });
      }
    } catch (error) {
      toast.error(getMessageError(error, 'Failed to load channel config'));
    }
  };

  const handlePreview = () => {};

  const handleNavigateAfterDelete = () => {
    navigate('/screen');
  };

  const renderButtons = () => {
    if (isCreateMode) {
      return (
        <Row>
          <Button
            type_new
            lg
            onClick={onSaveDraft}
            prefix={<IcCashierSave width={20} height={20} fill={Colors.primary[5]} />}
            type='button'
          >
            Draft
          </Button>
          <Button
            type_second
            lg
            type='submit'
            prefix={<IcPublish width={20} height={20} fill={Colors.neutral.bg[1]} />}
          >
            Publish
          </Button>
        </Row>
      );
    }

    switch (screenStatus) {
      case 'DRAFT':
        return (
          <Row>
            <Button
              type_second
              lg
              type='submit'
              prefix={<IcPublish width={20} height={20} fill={Colors.neutral.bg[1]} />}
            >
              Publish
            </Button>
          </Row>
        );

      // case 'ONLINE':
      //   return (
      //     <Row>
      //       <Button
      //         type_delete
      //         lg
      //         onClick={onPause}
      //         prefix={<IcPause width={20} height={20} fill={Colors.other[3]} />}
      //         type='button'
      //       >
      //         Pause
      //       </Button>
      //     </Row>
      //   );

      // case 'OFFLINE':
      //   return (
      //     <Row>
      //       <Button
      //         type_cancel
      //         lg
      //         onClick={onDeactivate}
      //         style={{ flex: 1 }}
      //         prefix={<IcClose width={20} height={20} />}
      //         type='button'
      //       >
      //         Deactivate
      //       </Button>
      //     </Row>
      //   );

      // case 'DEACTIVATED':
      //   return (
      //     <Row>
      //       <Button
      //         type_second
      //         lg
      //         onClick={onReactivate}
      //         style={{ flex: 1 }}
      //         prefix={<IcTick width={28} height={28} fill={Colors.neutral.bg[1]} />}
      //         type='button'
      //       >
      //         Reactivate
      //       </Button>
      //     </Row>
      //   );

      default:
        return null;
    }
  };
  return (
    <Container>
      <DropdownGroup>
        <LayoutSelectorWrapper>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const layoutCode = form.getFieldValue('layoutCode');

              return <LayoutSelector value={layoutCode || 'LANDSCAPE'} onChange={handleLayoutChange} />;
            }}
          </Form.Item>
        </LayoutSelectorWrapper>

        <Form.Item noStyle shouldUpdate>
          {() => {
            const storeId = form.getFieldValue('storeId') || '';
            return (
              <SelectWithLabel label='Store' value={storeId} onChange={handleStoreChange} options={storeOptions} />
            );
          }}
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {() => {
            const playListId = form.getFieldValue('playListId') || '';
            return (
              <SelectWithLabel
                label='Playlist'
                value={playListId}
                onChange={handlePlaylistChange}
                options={playlistOptions}
              />
            );
          }}
        </Form.Item>
      </DropdownGroup>

      {screenId && (
        <Button
          type_delete
          lg
          prefix={<IcTrash width={20} height={20} fill={Colors.other[3]} />}
          onClick={() => onDelete(screenId, handleNavigateAfterDelete)}
          type='button'
        >
          Delete
        </Button>
      )}
      <Button
        type_primary
        lg
        prefix={<IcShow width={20} height={20} fill={Colors.neutral.bg[1]} />}
        onClick={handlePreview}
        type='button'
      >
        Preview
      </Button>
      { screenId &&
        <Button
          type_new
          lg
          prefix={<IcCashierSave width={20} height={20} fill={Colors.primary[5]} />}
          onClick={onUpdate}
        >
          Save
        </Button>
      }
      {renderButtons()}
      {DeleteModal}
    </Container>
  );
};

export default EditorHeader;

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  flex-shrink: 0;
`;

const DropdownGroup = styled.div`
  display: flex;
  flex: 1;
  gap: 16px;
`;

const LayoutSelectorWrapper = styled.div`
  flex: 1;

  & > button {
    width: 100%;
  }
`;
